import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import PageWrapper from "../pageWrapper";
import { fetchWithTimeout } from "../../hooks/useFetchWithTimeout";
import useLazyLoading from "../../hooks/useLazyLoading";

//CSS
import "./unitPage.css";
import "../0PageStyles.css";

//Components
import Title from "../../Components/Other Components/Title";
import SearchBar from "../../Components/Other Components/searchBar";
import SortComponent from "../../Components/Other Components/sortComponent";
import UnitCreationPopup from "../../Components/Unit Components/unitCreationPopup";
import ClientEditPopup from "../../Components/Unit Components/unitEditPopup";
import ItemCardHeader from "../../Components/Item Components/itemCardHeader";
import ItemCard from "../../Components/Item Components/itemCard";
import PageLoader from "../../Components/Other Components/pageLoader";
import Button from "../../Components/Other Components/button";
//Functions and hooks
import { GetUnits } from "../../BackendAccess/UnitHooks";
import { useNotification } from "../../Contexts/NotificationContext";
import { useErrorHandler } from "../../hooks/errorHandler";
import useNarrowMode from "../../hooks/useNarrowModeChecker";
import { usePopup } from "../../Contexts/PopupContext";
import { useTranslation } from "react-i18next";

export default function UnitPage() {
  const navigate = useNavigate();
  const [units, setUnits] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageError, setPageError] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchFilteredItems, setSearchFilteredItems] = useState([]);

  const lazyLoadItemLoadCount = 20;
  const lazyLoadPixelThreshold = 200;

  const { showNotification } = useNotification();

  useErrorHandler(error, showNotification, setPageError);
  const isNarrow = useNarrowMode();

  const { showPopup, removePopup } = usePopup();

  const { t } = useTranslation();

  const openCreateUnitPopup = () => {
    const key = Date.now();
    showPopup(
      <UnitCreationPopup
        onCancel={() => removePopup(key)}
        onCreate={() => onCreatedUnit(key)}
      />
    );
  };
  const openEditUnitPopup = (id) => {
    const key = Date.now();
    showPopup(
      <ClientEditPopup
        onCancel={() => removePopup(key)}
        onEdit={() => onEditedUnit(key)}
        currentID={id}
        onDelete={() => onDeletedUnit(key)}
      />,
      key
    );
  };

  const fetchUnits = async () => {
    setLoading(true);
    try {
      const [data, error] = await fetchWithTimeout(GetUnits);
      if (error) {
        error.isPageError = true;
        setError(error);
      } else {
        setUnits(data);
      }
    } catch (err) {
      setError({
        err: t("server.Server error.", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnits();
  }, []);

  const handleEditClick = async (id) => {
    openEditUnitPopup(id);
  };
  const onDeletedUnit = async (key) => {
    await fetchUnits();
    removePopup(key);
  };
  const onEditedUnit = async (key) => {
    await fetchUnits();
    removePopup(key);
  };

  const handleCreateClick = async () => {
    openCreateUnitPopup();
  };
  const onCreatedUnit = async (key) => {
    fetchUnits();
    removePopup(key);
  };

  //***************************SEARCHING AND SORTING******************************
  // Handle search input
  const handleSearchChange = (term) => {
    setSearchTerm(term);
  };

  // Filter items based on search term
  useEffect(() => {
    const newSearchFilteredItems = units.filter((item) => {
      const lowerCaseTerm = searchTerm.toLowerCase();
      return (
        item.Name.toLowerCase().includes(lowerCaseTerm) ||
        item.UnitID.toString().toLowerCase().includes(lowerCaseTerm)
      );
    });
    setSearchFilteredItems(newSearchFilteredItems);
  }, [searchTerm, units]);

  const nameSortFunction = (direction) => {
    var unitsSorted = [...units].sort((a, b) =>
      a.Name.toUpperCase() > b.Name.toUpperCase()
        ? direction * 1
        : -1 * direction
    );
    setUnits(unitsSorted);
  };
  const idSortFunction = (direction) => {
    var unitsSorted = [...units].sort((a, b) =>
      a.UnitID > b.UnitID ? direction * 1 : -1 * direction
    );
    setUnits(unitsSorted);
  };
  ////////////////////////////////////////////////Lazy loading///////////////////////////////////////
  const { loadedItems, itemContainerRef } = useLazyLoading(
    searchFilteredItems,
    lazyLoadItemLoadCount,
    lazyLoadPixelThreshold
  );
  ////////////////////////////////////////////////Lazy loading end///////////////////////////////////////

  ////////////////////////////////////////Creating cards///////////////////////////////////////////
  const propertiesToShow = {
    ID: { Show: true, Width: 1 },
    Name: { Show: true, Width: 4 },
  };

  const columnWidths = Object.values(propertiesToShow).map(
    (item) => item.Width
  );
  const createHeaderCardProps = () => {
    const cardPropArray = [
      { Value: "ID", columnWidth: columnWidths[0] },
      { Value: "Name", columnWidth: columnWidths[1] },
    ];

    const newCardPropArr = cardPropArray.filter((item) => {
      return propertiesToShow[item.Value].Show;
    });
    return newCardPropArr;
  };
  const createCardProps = (item) => {
    const cardPropArray = [
      { Name: "ID", Value: item.UnitID, columnWidth: columnWidths[0] },
      { Name: "Name", Value: item.Name, columnWidth: columnWidths[1] },
    ];

    const newCardPropArr = cardPropArray.filter((item) => {
      return propertiesToShow[item.Name].Show;
    });
    return newCardPropArr;
  };

  ////////////////////////////////////////Creating cards end///////////////////////////////////////////

  const tutorialSteps = [
    {
      target: null,
      title: t("Your units", { ns: "tutorial" }),
      message: t(
        "This is your Units page - units define how items are measured and counted, like pieces, kilograms, or liters. Every item picks one of these when it's created.",
        { ns: "tutorial" }
      ),
    },
    {
      target: ".search-bar-container",
      title: t("Search", { ns: "tutorial" }),
      message: t("Search for a unit by name or ID.", { ns: "tutorial" }),
    },
    {
      target: ".CardHeader .btn-positive",
      title: t("Create a unit", { ns: "tutorial" }),
      message: t(
        "Opens a small popup where you name the new unit.",
        { ns: "tutorial" }
      ),
    },
    {
      target: ".item-card-container .Card:first-child",
      title: t("Unit rows", { ns: "tutorial" }),
      message: t(
        "Each row is one unit. Click Edit on a row to rename or delete it.",
        { ns: "tutorial" }
      ),
    },
  ];

  return (
    <PageWrapper tutorialId="units" tutorialSteps={tutorialSteps}>
      <PageLoader loading={loading} error={pageError}>
        <div className="page-content">
          <Title className="mt-3">Units</Title>

          <div className="search-bar-container">
            <SearchBar
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              placeholder={t("Search units...", { ns: "units" })}
            />
          </div>

          <div className="sortAndFilterBar">
            <SortComponent
              sortOpts={["Name", "ID"]}
              sortOptFunctions={[nameSortFunction, idSortFunction]}
            />
          </div>

          <div className="item-card-and-header-container">
            <ItemCardHeader
              propertiesToShow={createHeaderCardProps()}
              bBtnVisible={true}
              bBtnSectionVisible={true}
              btnSectionWidth={1}
              onClick={handleCreateClick}
              btnText={"Create"}
            />
            <div className="item-card-container" ref={itemContainerRef}>
              {loadedItems.map((item) => {
                return (
                  <ItemCard
                    key={item.UnitID}
                    ItemID={item.UnitID}
                    propertiesToShow={createCardProps(item)}
                    bBtnVisible={true}
                    bBtnSectionVisible={true}
                    btnSectionWidth={1}
                    onClick={() => {
                      handleEditClick(item.UnitID);
                    }}
                    btnText={t("Edit")}
                  />
                );
              })}
            </div>
          </div>
          {isNarrow ? (
            <div className="item-view-narrow-btn-container">
              <Button
                btnStyle={"btn-positive"}
                icon="create"
                onClick={handleCreateClick}
              >
                {"Create"}
              </Button>
            </div>
          ) : null}
        </div>
      </PageLoader>
    </PageWrapper>
  );
}
